import { useEffect, useState } from 'react';
import type { AdminApiOrganization } from '@/types/serializers';
import useFetchAdminPlans from '../../api/useFetchAdminPlans';
import useUpdateAdminOrganizationPlan from '../../api/useUpdateAdminOrganizationPlan';
import { AdminButton } from '../AdminButton';
import AdminFormInput from '../atoms/AdminFormInput';
import AdminFormSelect from '../atoms/AdminFormSelect';
import AdminErrorMessage from '../atoms/AdminErrorMessage';
import AdminModal from '../molecules/AdminModal';
import { toDateInputValue } from '../../utils/formatters';

type AdminOrganizationPlanEditModalProps = {
  open: boolean;
  onClose: () => void;
  organization: AdminApiOrganization;
};

export default function AdminOrganizationPlanEditModal({
  open,
  onClose,
  organization,
}: AdminOrganizationPlanEditModalProps) {
  const {
    data: plans = [],
    isLoading: isPlansLoading,
    isError: isPlansError,
  } = useFetchAdminPlans();
  const { mutateAsync, isPending } = useUpdateAdminOrganizationPlan();
  const [planId, setPlanId] = useState<number | null>(organization.plan_id ?? null);
  const [startDate, setStartDate] = useState(toDateInputValue(organization.contract_start_date));
  const [endDate, setEndDate] = useState(toDateInputValue(organization.contract_end_date));
  const [memberCounts, setMemberCounts] = useState(
    organization.member_counts ? String(organization.member_counts) : ''
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPlanId(organization.plan_id ?? null);
      setStartDate(toDateInputValue(organization.contract_start_date));
      setEndDate(toDateInputValue(organization.contract_end_date));
      setMemberCounts(organization.member_counts ? String(organization.member_counts) : '');
      setError(null);
    }
  }, [open, organization]);

  useEffect(() => {
    if (open && planId === null && plans.length > 0) {
      setPlanId(plans[0].id);
    }
  }, [open, planId, plans]);

  const handleSubmit = async () => {
    if (!planId) {
      setError('プランを選択してください');
      return;
    }
    const memberCountsValue = memberCounts.trim() === '' ? null : Number(memberCounts);
    if (memberCountsValue !== null && Number.isNaN(memberCountsValue)) {
      setError('契約人数は数値で入力してください');
      return;
    }
    try {
      await mutateAsync({
        organizationId: organization.id,
        planId,
        startDate,
        endDate,
        memberCounts: memberCountsValue,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="プランの編集"
      maxWidth="max-w-screen-sm"
      footer={
        <div className="flex justify-end">
          <AdminButton
            label={isPending ? '保存中...' : '保存する'}
            disabled={isPending}
            onClick={handleSubmit}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <AdminFormSelect
          label="プラン"
          value={planId ?? ''}
          onChange={(e) => setPlanId(Number(e.target.value))}
          isLoading={isPlansLoading}
          isError={isPlansError}
        >
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </AdminFormSelect>
        <div className="flex gap-4">
          <div className="flex-1">
            <AdminFormInput
              label="契約開始日"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <AdminFormInput
              label="契約終了日"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <AdminFormInput
          label="契約人数"
          type="number"
          min={1}
          value={memberCounts}
          onChange={(e) => setMemberCounts(e.target.value)}
        />
        <AdminErrorMessage message={error} />
      </div>
    </AdminModal>
  );
}
